
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DATASOURCE_SCHEMA } from '../data/datasourceSchema';
import { 
    Plus, Trash2, Database, Filter, Box, X, Search, 
    Settings2, ChevronRight, Check, AlertCircle, Play, Loader2, ChevronDown
} from 'lucide-react';

interface DataSourceBuilderProps {
  value: string;
  onChange: (value: string) => void;
}

interface ParsedFilter {
  id: string;
  name: string;
  args: string[];
}

interface FetchParams {
    filter: string;
    sort: string;
    page: number;
}
type RelationCallback = (entity: string, params: FetchParams) => Promise<{ id: string; label: string }[]>;

const mockFetchRelations: RelationCallback = async (entity, params) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 600)); 
    
    // Simulate fetching based on entity type
    const baseItems = Array.from({ length: 15 }).map((_, i) => {
        const idx = i + 1 + (params.page * 10);
        return {
            id: `uuid-0000-${idx.toString().padStart(4, '0')}`,
            label: `${entity} Item ${idx} ${params.filter ? `(${params.filter})` : ''}`
        };
    });

    if (params.filter) {
        return baseItems.filter(i => i.label.toLowerCase().includes(params.filter.toLowerCase()));
    }
    return baseItems;
};


export const DataSourceBuilder: React.FC<DataSourceBuilderProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // -- Trigger Parsing Logic --
  const previewParts = useMemo(() => {
      if (!value) return { source: null, filters: 0, model: null };
      const parts = value.split('::');
      return {
          source: parts[0],
          filters: parts.filter(p => p.includes('(') || (p !== parts[0] && p !== parts[parts.length - 1])).length, // Rough estimate
          model: parts.length > 1 && !parts[parts.length - 1].includes('(') ? parts[parts.length - 1] : null
      };
  }, [value]);

  return (
    <>
        {/* Trigger UI */}
        <div 
            onClick={() => setIsOpen(true)}
            className="group relative w-full border border-gray-200 rounded-lg p-3 bg-white hover:border-brand-300 hover:shadow-sm transition-all cursor-pointer"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Database size={14} className="text-brand-600" />
                    <span>Data Source</span>
                </div>
                <Settings2 size={14} className="text-gray-400 group-hover:text-brand-500" />
            </div>
            
            {value ? (
                <div className="space-y-1">
                    <div className="text-xs font-mono bg-gray-50 px-2 py-1 rounded text-gray-600 truncate border border-gray-100">
                        {previewParts.source}
                    </div>
                    {previewParts.model && (
                         <div className="flex items-center gap-1 text-[10px] text-gray-500 px-1">
                            <Box size={10} />
                            <span>Model: {previewParts.model}</span>
                         </div>
                    )}
                </div>
            ) : (
                <div className="text-xs text-gray-400 italic px-1">
                    Not configured
                </div>
            )}
        </div>

        {/* Modal Portal */}
        {isOpen && createPortal(
            <DataSourceModal 
                initialValue={value} 
                onClose={() => setIsOpen(false)} 
                onApply={(val) => { onChange(val); setIsOpen(false); }} 
            />,
            document.body
        )}
    </>
  );
};

// --- Relation Picker Component ---

interface RelationPickerProps {
    entity: string;
    value: string; // Semicolon separated UUIDs
    onChange: (val: string) => void;
    multi?: boolean;
}

const RelationPicker: React.FC<RelationPickerProps> = ({ entity, value, onChange, multi }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [items, setItems] = useState<{ id: string; label: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedIds = useMemo(() => value ? value.split(';').filter(Boolean) : [], [value]);

    useEffect(() => {
        if (isOpen) {
            fetchItems();
        }
    }, [isOpen, search]); // Debouncing would be better in prod

    // Handle clicks outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const data = await mockFetchRelations(entity, { filter: search, sort: 'name', page: 0 });
            setItems(data);
        } finally {
            setLoading(false);
        }
    };

    const toggleItem = (id: string) => {
        if (multi) {
            if (selectedIds.includes(id)) {
                onChange(selectedIds.filter(i => i !== id).join(';'));
            } else {
                onChange([...selectedIds, id].join(';'));
            }
        } else {
            onChange(id);
            setIsOpen(false);
        }
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <div 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white cursor-pointer min-h-[32px] flex items-center justify-between"
            >
                <div className="flex flex-wrap gap-1 truncate">
                    {selectedIds.length > 0 ? (
                        selectedIds.map(id => (
                            <span key={id} className="text-[10px] bg-brand-50 text-brand-700 px-1 rounded border border-brand-100">
                                {id.substring(0, 8)}...
                            </span>
                        ))
                    ) : (
                        <span className="text-gray-400">Select {entity}...</span>
                    )}
                </div>
                <ChevronDown size={14} className="text-gray-400" />
            </div>

            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 flex flex-col">
                    <div className="p-2 border-b border-gray-100">
                        <input 
                            autoFocus
                            placeholder="Search..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded"
                        />
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="p-4 flex justify-center text-gray-400">
                                <Loader2 size={16} className="animate-spin" />
                            </div>
                        ) : (
                            items.map(item => (
                                <div 
                                    key={item.id}
                                    onClick={() => toggleItem(item.id)}
                                    className="px-3 py-2 text-xs hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                                >
                                    <div className={`w-3 h-3 border rounded-sm flex items-center justify-center ${selectedIds.includes(item.id) ? 'bg-brand-500 border-brand-500' : 'border-gray-300'}`}>
                                        {selectedIds.includes(item.id) && <Check size={10} className="text-white" />}
                                    </div>
                                    <span className="truncate">{item.label}</span>
                                </div>
                            ))
                        )}
                        {!loading && items.length === 0 && (
                             <div className="p-4 text-center text-xs text-gray-400">No items found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};


// --- Internal Modal Component ---

interface DataSourceModalProps {
    initialValue: string;
    onClose: () => void;
    onApply: (val: string) => void;
}

const DataSourceModal: React.FC<DataSourceModalProps> = ({ initialValue, onClose, onApply }) => {
    // Editor State
    const [selectedSource, setSelectedSource] = useState<string>('');
    const [filters, setFilters] = useState<ParsedFilter[]>([]);
    const [selectedModel, setSelectedModel] = useState<string>('');
    
    // UI State
    const [sourceSearch, setSourceSearch] = useState('');
    const [filterSearch, setFilterSearch] = useState('');

    // Initialize State from String
    useEffect(() => {
        if (!initialValue) return;

        const parts = initialValue.split('::');
        if (parts.length === 0) return;

        const queryName = parts[0];
        
        // Match Query to Datasource Definition
        const def = DATASOURCE_SCHEMA.datasources.find(d => 
            d.name === queryName || d.queries.some((q: any) => q.name === queryName || q.alias === queryName)
        );

        const normalizedSource = def ? def.name : queryName;
        setSelectedSource(normalizedSource);

        let potentialFilters = parts.slice(1);
        let model = '';

        // Check if last part is a model
        const lastPart = potentialFilters[potentialFilters.length - 1];
        const isModel = def?.models.some(m => m.name === lastPart);
        
        if (lastPart && isModel) {
            model = lastPart;
            potentialFilters.pop();
        }
        setSelectedModel(model);

        // Parse Filters
        const parsed: ParsedFilter[] = potentialFilters.map(str => {
            const match = str.match(/^(\w+)(?:\((.*)\))?$/);
            if (match) {
                return {
                    id: Math.random().toString(36).substr(2, 9),
                    name: match[1],
                    // We need to be careful splitting args by comma if they contain comma separated values within? 
                    // Schema says uuid[] is comma or semicolon separated. But function args are comma separated.
                    // This simple regex splitting might break if args themselves contain comma. 
                    // Assuming for now args are simple or quoted if complex (but schema doesn't specify quoting).
                    args: match[2] ? match[2].split(',').map(s => s.trim()) : []
                };
            }
            return { id: Math.random().toString(36).substr(2, 9), name: str, args: [] };
        });
        setFilters(parsed);

    }, [initialValue]);

    // Construct String
    const resultString = useMemo(() => {
        if (!selectedSource) return '';
        let parts = [selectedSource];
        filters.forEach(f => {
            if (f.args.length > 0 && f.args.some(a => a)) {
                parts.push(`${f.name}(${f.args.join(', ')})`);
            } else {
                parts.push(f.name);
            }
        });
        if (selectedModel) parts.push(selectedModel);
        return parts.join('::');
    }, [selectedSource, filters, selectedModel]);

    // Actions
    const handleSourceSelect = (sourceName: string) => {
        setSelectedSource(sourceName);
        setFilters([]);
        setSelectedModel('');
        setSourceSearch('');
    };

    const addFilter = (filterName: string) => {
        const def = DATASOURCE_SCHEMA.datasources.find(d => d.name === selectedSource);
        const filterDef = def?.filters.find(f => f.name === filterName);
        const argsCount = filterDef?.parameters?.length || 0;
        
        setFilters([
            ...filters, 
            { 
                id: Math.random().toString(36).substr(2, 9), 
                name: filterName, 
                args: Array(argsCount).fill('') 
            }
        ]);
        setFilterSearch('');
    };

    const removeFilter = (id: string) => {
        setFilters(filters.filter(f => f.id !== id));
    };

    const updateFilterArg = (id: string, index: number, val: string) => {
        setFilters(filters.map(f => {
            if (f.id === id) {
                const newArgs = [...f.args];
                newArgs[index] = val;
                return { ...f, args: newArgs };
            }
            return f;
        }));
    };

    // Derived Data
    const currentDef = DATASOURCE_SCHEMA.datasources.find(d => d.name === selectedSource);
    
    const filteredSources = useMemo(() => {
        return DATASOURCE_SCHEMA.datasources.filter(d => 
            d.name.toLowerCase().includes(sourceSearch.toLowerCase()) ||
            d.description.toLowerCase().includes(sourceSearch.toLowerCase())
        );
    }, [sourceSearch]);

    const availableFilters = useMemo(() => {
        if (!currentDef) return [];
        return currentDef.filters.filter(f => 
            f.name.toLowerCase().includes(filterSearch.toLowerCase())
        );
    }, [currentDef, filterSearch]);

    // Helper to render input based on schema type
    const renderArgumentInput = (paramDef: any, value: string, onChange: (val: string) => void) => {
        if (!paramDef) return null;

        const { type, relation, referencedEntity } = paramDef;

        if (relation && referencedEntity) {
            const isMulti = type?.endsWith('[]');
            return (
                <RelationPicker 
                    entity={referencedEntity}
                    value={value}
                    onChange={onChange}
                    multi={isMulti}
                />
            );
        }

        if (type === 'bool') {
             return (
                <select 
                    value={value} 
                    onChange={e => onChange(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                >
                    <option value="">Select...</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                </select>
             );
        }

        if (type === 'int') {
            return (
                <input 
                    type="number"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Integer..."
                />
            );
        }

        return (
             <div className="relative">
                <input 
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                    placeholder={type?.endsWith('[]') ? "Use ; for multiple values..." : "Value..."}
                />
                {type?.endsWith('[]') && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">
                        Split by ;
                    </div>
                )}
             </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-5xl h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            <Database size={20} className="text-brand-600" />
                            Data Source Builder
                        </h2>
                        <p className="text-sm text-gray-500">Construct dynamic data queries for your field</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full text-gray-500 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Main Content (2 Columns) */}
                <div className="flex-1 flex overflow-hidden">
                    
                    {/* Left Sidebar: Source Selection */}
                    <div className="w-1/3 border-r border-gray-200 flex flex-col bg-gray-50/50">
                        <div className="p-4 border-b border-gray-200 bg-white">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input 
                                    type="text" 
                                    placeholder="Search data sources..." 
                                    value={sourceSearch}
                                    onChange={(e) => setSourceSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {filteredSources.map(ds => (
                                <button
                                    key={ds.name}
                                    onClick={() => handleSourceSelect(ds.name)}
                                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors flex items-start gap-3 ${selectedSource === ds.name ? 'bg-brand-50 border-brand-100' : ''}`}
                                >
                                    <div className={`mt-0.5 p-1.5 rounded-md ${selectedSource === ds.name ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-400'}`}>
                                        <Database size={16} />
                                    </div>
                                    <div>
                                        <div className={`font-medium text-sm ${selectedSource === ds.name ? 'text-brand-900' : 'text-gray-700'}`}>
                                            {ds.name}
                                        </div>
                                        <div className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                                            {ds.description}
                                        </div>
                                    </div>
                                    {selectedSource === ds.name && <ChevronRight size={16} className="ml-auto self-center text-brand-500" />}
                                </button>
                            ))}
                            {filteredSources.length === 0 && (
                                <div className="p-8 text-center text-gray-400 text-sm">
                                    No data sources found matching "{sourceSearch}"
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Area: Configuration */}
                    <div className="flex-1 flex flex-col bg-white overflow-hidden">
                        {!selectedSource ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                                <Database size={48} className="mb-4 opacity-20" />
                                <p className="font-medium">Select a Data Source to begin</p>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col overflow-hidden">
                                {/* Configuration Scrollable Area */}
                                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                                    
                                    {/* Section 1: Output Model */}
                                    <section>
                                        <div className="flex items-center gap-2 text-sm font-bold text-gray-800 uppercase tracking-wide mb-3">
                                            <Box size={16} className="text-brand-500" />
                                            Output Model
                                        </div>
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <p className="text-xs text-gray-500 mb-2">
                                                Select the data structure to return. Leave empty for default.
                                            </p>
                                            <select
                                                value={selectedModel}
                                                onChange={(e) => setSelectedModel(e.target.value)}
                                                className="w-full max-w-md px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm focus:ring-brand-500 focus:border-brand-500"
                                            >
                                                <option value="">Default Model</option>
                                                {currentDef?.models.map(m => (
                                                    <option key={m.name} value={m.name}>{m.name} — {m.description}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </section>

                                    {/* Section 2: Filters */}
                                    <section className="flex-1 flex flex-col min-h-0">
                                        <div className="flex items-center gap-2 text-sm font-bold text-gray-800 uppercase tracking-wide mb-3">
                                            <Filter size={16} className="text-brand-500" />
                                            Data Filters
                                        </div>
                                        
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                                            {/* Active Filters Column */}
                                            <div className="flex flex-col gap-3">
                                                <h4 className="text-xs font-semibold text-gray-500 uppercase">Active Pipeline</h4>
                                                
                                                {filters.length === 0 ? (
                                                     <div className="p-6 border-2 border-dashed border-gray-200 rounded-lg text-center text-gray-400 text-sm bg-gray-50">
                                                        No filters applied. Data will be unfiltered.
                                                     </div>
                                                ) : (
                                                    <div className="space-y-3">
                                                        {filters.map((filter, idx) => {
                                                            const def = currentDef?.filters.find(f => f.name === filter.name);
                                                            return (
                                                                <div key={filter.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 group hover:border-brand-300 transition-colors">
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <div>
                                                                            <span className="font-bold text-gray-800 text-sm">{filter.name}</span>
                                                                            {def?.description && <p className="text-xs text-gray-500 mt-0.5">{def.description}</p>}
                                                                        </div>
                                                                        <button onClick={() => removeFilter(filter.id)} className="text-gray-400 hover:text-red-500">
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </div>
                                                                    {filter.args.length > 0 && (
                                                                        <div className="grid grid-cols-1 gap-2 mt-2 pt-2 border-t border-gray-100">
                                                                            {filter.args.map((arg, argIdx) => {
                                                                                // Handle cases where schema has Objects for parameters or simple Strings (old schema vs new schema compat)
                                                                                const paramDef = def?.parameters?.[argIdx];
                                                                                const paramName = typeof paramDef === 'string' ? paramDef : (paramDef?.name || `Arg ${argIdx + 1}`);
                                                                                
                                                                                // If it's the new object style schema
                                                                                const isObjectSchema = typeof paramDef === 'object';
                                                                                
                                                                                return (
                                                                                    <div key={argIdx}>
                                                                                        <label className="text-[10px] uppercase font-bold text-gray-400 mb-0.5 block flex items-center justify-between">
                                                                                            {paramName}
                                                                                            {isObjectSchema && paramDef.type && <span className="font-mono text-[9px] bg-gray-100 px-1 rounded">{paramDef.type}</span>}
                                                                                        </label>
                                                                                        
                                                                                        {isObjectSchema ? (
                                                                                            renderArgumentInput(paramDef, arg, (val) => updateFilterArg(filter.id, argIdx, val))
                                                                                        ) : (
                                                                                             <input 
                                                                                                type="text"
                                                                                                value={arg}
                                                                                                onChange={(e) => updateFilterArg(filter.id, argIdx, e.target.value)}
                                                                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                                                                                placeholder="Value..."
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                    <div className="flex justify-center mt-2">
                                                                        <div className="h-4 w-0.5 bg-gray-200 group-last:hidden"></div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Available Filters Column */}
                                            <div className="flex flex-col gap-3 h-full">
                                                <div className="flex justify-between items-center">
                                                    <h4 className="text-xs font-semibold text-gray-500 uppercase">Available Filters</h4>
                                                    <div className="relative w-40">
                                                        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                                                        <input 
                                                            type="text" 
                                                            placeholder="Filter list..."
                                                            value={filterSearch}
                                                            onChange={(e) => setFilterSearch(e.target.value)}
                                                            className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:bg-white"
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg overflow-y-auto p-2 space-y-2">
                                                    {availableFilters.map(f => (
                                                        <button 
                                                            key={f.name}
                                                            onClick={() => addFilter(f.name)}
                                                            className="w-full text-left p-2.5 bg-white border border-gray-200 rounded hover:border-brand-300 hover:shadow-sm transition-all group"
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <span className="font-semibold text-sm text-gray-700 group-hover:text-brand-700">{f.name}</span>
                                                                <Plus size={14} className="text-gray-300 group-hover:text-brand-500" />
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{f.description}</p>
                                                            {f.parameters && f.parameters.length > 0 && (
                                                                <div className="mt-2 flex flex-wrap gap-1">
                                                                    {f.parameters.map((p: any) => {
                                                                        const pName = typeof p === 'string' ? p : p.name;
                                                                        return (
                                                                            <span key={pName} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">
                                                                                {pName}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </button>
                                                    ))}
                                                    {availableFilters.length === 0 && (
                                                        <div className="text-center text-gray-400 text-xs py-4">
                                                            No filters match your search.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-4">
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Generated String</label>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded text-sm font-mono text-gray-700 overflow-x-auto whitespace-nowrap">
                                {resultString || <span className="text-gray-400 italic">Select a source to generate...</span>}
                            </code>
                        </div>
                    </div>
                    <div className="flex items-end gap-3 self-end">
                        <button 
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={() => onApply(resultString)}
                            disabled={!selectedSource}
                            className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <Check size={18} />
                            Apply Configuration
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};
